<template>
  <section class="registration-page">
    <div class="registration-page__welcome">
      <img
        class="welcome-image"
        src="../assets/registration/welcome-gyre.png"
        alt="Welcome to Gyre"
      />
      <div class="registration-footer">
        <ul class="registration-footer-list">
          <li class="registration-footer-item">
            <a role="link" href="https://gyreteams.com">About</a>
          </li>
          <li class="registration-footer-item">
            <a role="link" href="https://www.gyreteams.com/terms-of-service">Terms of Service</a>
          </li>
          <li class="registration-footer-item">
            <a role="link" href="https://gyreteams.com/contact">Contact</a>
          </li>
        </ul>
      </div>
    </div>
    <article v-if="!registerSuccess" class="registration-page-right">
      <div
        v-if="isViewpointsInvite && viewpointsSubjectName.length > 0"
        class="viewpoints-invite-box"
      >
        <p>
          <strong>{{ viewpointsSubjectName }}</strong> has invited you to complete a review of
          {{ viewpointsSubjectFirstName }}'s character strengths. This will help
          {{ viewpointsSubjectFirstName }} build an accurate self-view.
          <a href="https://gyreteams.com" target="_blank" rel="noopener noreferrer">Learn more</a>
        </p>
      </div>
      <div class="registration-form-container">
        <div>
          <h1>Register</h1>
          <span
            >Already have an account?
            <a @click="goToSignIn" href="javascript:void(0)" data-test="rwi-sign-in"
              >Sign in</a
            ></span
          >
          <h5 v-if="isInvitedToTeam">
            Register your account to join {{ model?.groupToJoin.name }}.
          </h5>
          <h5 v-if="isInvitedToSubscription">
            Register your account to join your organisation on Gyre
          </h5>
        </div>
        <form
          method="post"
          @submit.prevent="withInvite ? submitForm() : checkReCaptcha()"
          class="registration-form"
          :class="{ 'registration-form--with-invite': withInvite }"
        >
          <div class="name-fields">
            <!-- First name -->
            <div class="registration-label-section">
              <label for="first-name">First name</label>
              <div
                v-if="v$.firstName?.required.$invalid && submitStatus === 'Error'"
                style="color: red"
              >
                Required field
              </div>
              <div
                v-if="v$.firstName?.maxLength.$invalid && submitStatus === 'Error'"
                style="color: red"
              >
                Maximum of 50 characters allowed
              </div>
              <div class="name-field">
                <input
                  id="first-name"
                  class="registration-text-input"
                  type="text"
                  placeholder="First name"
                  name="first-name"
                  v-model.trim="firstName"
                  @blur="v$.firstName?.$touch()"
                  data-test="rwi-first-name"
                />
                <i class="placeholder-icon"
                  ><img src="../assets/registration/user-icon.svg" alt="User icon"
                /></i>
              </div>
            </div>
            <!-- End of First name -->
            <!-- Last name -->
            <div class="registration-label-section">
              <label for="last-name">Last name</label>
              <div
                v-if="v$.lastName?.required.$invalid && submitStatus === 'Error'"
                style="color: red"
              >
                Required field
              </div>
              <div
                v-if="v$.lastName?.maxLength.$invalid && submitStatus === 'Error'"
                style="color: red"
              >
                Maximum of 50 characters allowed
              </div>
              <div class="name-field">
                <input
                  id="last-name"
                  class="registration-text-input"
                  type="text"
                  placeholder="Last name"
                  name="last-name"
                  v-model.trim="lastName"
                  @blur="v$.lastName?.$touch()"
                  data-test="rwi-last-name"
                />
                <i class="placeholder-icon"
                  ><img src="../assets/registration/user-icon.svg" alt="User icon"
                /></i>
              </div>
            </div>
            <!-- End of Last name -->
          </div>
          <!-- Email -->
          <div class="registration-label-section">
            <label for="email">Work email</label>
            <div
              v-if="v$.userEmail?.email.$invalid && submitStatus === 'Error'"
              style="color: red"
              data-test="invalid-email"
            >
              Invalid email address
            </div>
            <div
              v-if="v$.userEmail?.required.$invalid && submitStatus === 'Error'"
              style="color: red"
            >
              Required field
            </div>
            <div
              v-if="v$.userEmail?.maxLength.$invalid && submitStatus === 'Error'"
              style="color: red"
            >
              Maximum of 70 characters allowed
            </div>
            <input
              id="email"
              class="registration-text-input"
              placeholder="Enter work email"
              name="email"
              v-model.trim="userEmail"
              @blur="v$.userEmail?.$touch()"
              data-test="rwi-email"
            />
            <i class="placeholder-icon"
              ><img src="../assets/registration/email-icon.svg" alt="email icon"
            /></i>
          </div>
          <!-- End of Email -->
          <!-- Password -->
          <div class="registration-label-section">
            <span>
              <label id="password-label" for="password">Password</label>
              <img
                src="../assets/registration/info-icon.svg"
                alt="password rules information"
                tabindex="0"
                id="password-icon"
                class="info-icon"
              />
              <tooltip
                text="Must be a minimum of 8 characters in length. Must include at least one number, one uppercase and one lowercase letter. No spaces at the beginning or end."
                data-html="true"
                target="#password-icon"
                custom-class="password-tooltip"
              />
            </span>

            <div
              v-if="
                (v$.password?.minLength.$invalid || v$.password?.required.$invalid) &&
                submitStatus === 'Error'
              "
              style="color: red"
              data-test="min-length-password"
            >
              Minimum 8 characters long.
            </div>
            <div
              v-if="v$.password?.isPasswordValid.$invalid && submitStatus === 'Error'"
              style="color: red"
              data-test="invalid-password"
            >
              Must include a number, an upper and a lower case letter.
            </div>
            <div
              v-if="v$.password?.maxLength.$invalid && submitStatus === 'Error'"
              style="color: red"
            >
              Maximum of 40 characters allowed
            </div>
            <input
              aria-labelledby="password-label"
              class="registration-text-input"
              type="password"
              placeholder="Password"
              v-model.trim="password"
              id="password"
              @blur="v$.password?.$touch()"
              data-test="rwi-password"
            />
            <i class="placeholder-icon"
              ><img src="../assets/registration/lock-icon.svg" alt="lock icon"
            /></i>
          </div>
          <!-- End of Password -->
          <!-- Confirm password -->
          <div class="registration-label-section">
            <label for="confirm">Confirm</label>
            <div
              v-if="v$.confirmedPassword?.sameAsPassword.$invalid && submitStatus === 'Error'"
              style="color: red"
              data-test="mismatched-passwords"
            >
              Those passwords didn't match. Try again.
            </div>
            <input
              id="confirm"
              class="registration-text-input"
              type="password"
              placeholder="Confirm password"
              v-model.trim="confirmedPassword"
              @blur="v$.confirmedPassword?.$touch()"
              data-test="rwi-password-confirm"
            />
            <i class="placeholder-icon"
              ><img src="../assets/registration/lock-icon.svg" alt="lock icon"
            /></i>
          </div>
          <!-- End of Confirm password -->

          <!-- Company Name-->
          <div class="registration-label-section">
            <label for="company-name">Company name</label>
            <div
              v-if="v$.companyName?.minLength.$invalid && submitStatus === 'Error'"
              style="color: red"
            >
              Company name should be at least 2 characters long
            </div>
            <div
              v-if="v$.companyName?.maxLength.$invalid && submitStatus === 'Error'"
              style="color: red"
            >
              Maximum of 40 characters allowed
            </div>
            <div
              v-if="v$.companyName?.required.$invalid && submitStatus === 'Error'"
              style="color: red"
              data-test="required-company-name"
            >
              Required field
            </div>
            <input
              id="company-name"
              class="registration-text-input"
              type="text"
              placeholder="Company name"
              v-model.trim="companyName"
              @blur="v$.companyName?.$touch()"
              data-test="rwi-company-name"
            />
            <i class="placeholder-icon"
              ><img src="../assets/registration/company-icon.svg" alt="company icon"
            /></i>
          </div>
          <!--End of Company Name-->

          <div class="checkboxes">
            <!-- Offers/marketing checkbox -->
            <div class="checkbox-default">
              <input
                type="checkbox"
                name="offers-checkbox"
                id="offers-checkbox"
                v-model="isOffersChecked"
              />
              <label for="offers-checkbox"
                >(Optional) I'd like to hear about Gyre offers and updates</label
              >
            </div>
            <!-- Terms checkbox -->
            <div class="checkbox-default">
              <input
                type="checkbox"
                name="terms-checkbox"
                id="terms-checkbox"
                v-model="isTermsChecked"
                @change="v$.isTermsChecked?.$touch()"
                :class="{ required: !v$.isTermsChecked?.$invalid && attemptedToSubmit }"
                data-test="rwi-terms"
              />
              <span
                v-if="v$.isTermsChecked?.$invalid && attemptedToSubmit"
                style="color: red; margin-right: 0.5rem"
              >
                Required.
              </span>
              <label for="terms-checkbox"
                >I agree to the
                <span class="terms-link"
                  ><a href="https://www.gyreteams.com/terms-of-service">terms of service</a></span
                ></label
              >
            </div>
          </div>
          <span v-if="!!serverErrorOccurred" style="color: red"
            >Something went wrong, please try again later.</span
          >
          <span v-if="recaptchaError" style="color: red"
            >Recaptcha failed to verify your identity. Please refresh the page and try again.</span
          >
          <span style="margin-top: 20px" v-else-if="emailAlreadyExists"
            >This email already exists in Gyre. <br />
            <a @click="goToSignIn" href="javascript:void(0)">Sign in</a
            ><span v-if="withInvite"> before accepting this invite.</span></span
          >
          <SubmitButton
            class="registration-submit-button"
            buttonClass="btn-primary"
            :is-submitted="isSubmitted"
            data-form-type="action,register"
            :use-recaptcha="true"
            data-test="rwi-submit"
            :disabled="false"
            >{{ isViewpointsInvite ? "Register & Accept Invite" : "Register" }}
          </SubmitButton>
        </form>
      </div>
    </article>
    <article v-if="registerSuccess" class="registration-page-right success">
      <div class="register-success">
        <img class="success-gif" src="../assets/registration/Tick-GIF.gif" alt="success tick" />
        <h2>Success!</h2>
        <p>You have successfully registered. You'll be redirected shortly...</p>
      </div>
      <!-- For when we add two factor auth -->
      <!-- <p class="resend-email-message">
        Didn't receive an email? <span class="resend-email">Click here</span> to resend.
      </p> -->
    </article>
  </section>
</template>
<script setup lang="ts">
import axios, { AxiosResponse, AxiosError } from "axios";
import { debug as d } from "debug";
import { Tooltip } from "uiv";
import useVuelidate from "@vuelidate/core";
import { computed, onMounted, ref } from "vue";
import { email, maxLength, minLength, required, sameAs } from "@vuelidate/validators";
import { useRoute } from "vue-router";
import SubmitButton from "../components/submit-button.vue";
import { InviteCodeSubjectApiModel, RegisterApiModel } from "../api-models";

declare let grecaptcha: any;

const debug = d("cs.RegistrationPage");

const isPasswordValid = (value: string): boolean => {
  const passwordRegex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])/;
  const passwordArray = value.match(passwordRegex);
  if (passwordArray) {
    return passwordArray?.length > 0;
  }
  return false;
};

const props = defineProps<{ code?: string }>();

const isOffersChecked = ref(false);
const isTermsChecked = ref(false);
const emailAlreadyExists = ref(false);
const serverErrorOccurred = ref("");
const firstName = ref("");
const lastName = ref("");
// TODO: rename this so it doesn't clash with vuelidate variable
const userEmail = ref("");
const password = ref("");
const confirmedPassword = ref("");
const companyName = ref("");
const attemptedToSubmit = ref(false);
const registerSuccess = ref(false);
const recaptchaError = ref(false);
const recaptchaToken = ref("");
const submitStatus = ref("");
const viewpointsSubjectName = ref("");
const viewpointsSubjectFirstName = ref("");
const model = ref<RegisterApiModel | null>(null);

const route = useRoute();

const rules = {
  firstName: { required, maxLength: maxLength(50) },
  lastName: { required, maxLength: maxLength(50) },
  userEmail: { required, email, maxLength: maxLength(70) },
  companyName: { required, minLength: minLength(2), maxLength: maxLength(50) },
  password: { required, minLength: minLength(8), isPasswordValid, maxLength: maxLength(40) },
  confirmedPassword: { required, sameAsPassword: sameAs(password) },
  isTermsChecked: { required, sameAs: (value: boolean) => value },
};

const state = {
  firstName,
  lastName,
  userEmail,
  companyName,
  password,
  confirmedPassword,
  isTermsChecked,
};

const v$ = useVuelidate(rules, state, { $autoDirty: true });

const withInvite = computed(() => !!props.code);
const isViewpointsInvite = computed(() => withInvite.value && props.code?.includes("review"));

const siteKey = "6LfOOSYfAAAAANE6YgdhrYFpz7jtjLdV5wdkYHgi";
const googleRecaptchaScript = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;

const canSubmit = computed(() => {
  if (v$.value) {
    return !v$.value.$invalid && !!isTermsChecked.value;
  }

  return false;
});

const isInvitedToTeam = computed(() => withInvite.value && !!model.value?.groupToJoin);
const isInvitedToSubscription = computed(
  () => withInvite.value && !!model.value?.isSubscriptionInvite,
);

function goToSignIn(): void {
  const encodedRedirectUrl = encodeURIComponent(
    isViewpointsInvite.value ? `/invite/accept/${props.code}` : "",
  );

  window.location.href = isViewpointsInvite.value
    ? `/login?ReturnUrl=${encodedRedirectUrl}`
    : "/login";
}

function redirect(location: string): void {
  window.location.href = location;
}

function handleInvalidSource(msg?: string): void {
  if (!msg || !msg.includes("Invalid source")) return;

  serverErrorOccurred.value = "There was an error, please try again.";
  sessionStorage.removeItem("utm_source");
}

function registerFromInvite(data: RegisterApiModel): void {
  axios
    .post(
      `/account/register?id=${props.code}&skipJoined=${route.query.skipJoined ?? "false"}`,
      data,
    )
    .then((res: AxiosResponse) => {
      registerSuccess.value = true;
      redirect(res.headers.location ?? "/home");
    })
    .catch((e: AxiosError) => {
      debug(e);
      const statusCode = e.response?.status;
      submitStatus.value = "";
      if (statusCode === 409) {
        emailAlreadyExists.value = true;
      } else {
        handleInvalidSource(e.response?.statusText);
        serverErrorOccurred.value = "There was a server error. Please try again";
      }
    });
}

function register(data: RegisterApiModel): void {
  axios
    .post("/api/register", data)
    .then((res: AxiosResponse) => {
      if (res.status === 200) {
        registerSuccess.value = true;
        setTimeout(() => {
          window.location.href = res.headers.location ?? "/foundations/default";
        }, 3000);
      }
    })
    .catch((e: AxiosError) => {
      debug(e);
      const statusCode = e.response?.status;
      submitStatus.value = "";
      if (statusCode === 409) {
        emailAlreadyExists.value = true;
      } else if (e.response?.statusText.includes("Recaptcha")) {
        recaptchaError.value = true;
      } else {
        handleInvalidSource(e.response?.statusText);
        serverErrorOccurred.value = String(e);
      }
    });
}

function handleRegistration(): void {
  attemptedToSubmit.value = true;
  if (!canSubmit.value) return;

  const source = sessionStorage.getItem("utm_source");

  const newUserData = {
    firstName: firstName.value.trim(),
    lastName: lastName.value.trim(),
    email: userEmail.value.trim(),
    companyName: companyName.value.trim(),
    password: confirmedPassword.value,
    optInForMarketing: isOffersChecked.value,
    recaptchaToken: recaptchaToken.value,
    source: source ?? "",
  } as RegisterApiModel;

  if (withInvite.value) {
    registerFromInvite(newUserData);
  } else {
    register(newUserData);
  }
}

function submitForm(): void {
  attemptedToSubmit.value = true;
  debug(v$.value.$invalid);
  if (v$.value.$invalid) {
    submitStatus.value = "Error";
  } else {
    submitStatus.value = "OK";
    handleRegistration();
  }
}

async function setRecaptcha(): Promise<void> {
  await grecaptcha.execute(siteKey, { action: "Register" }).then((token: string) => {
    recaptchaToken.value = token;
  });
}

function checkReCaptcha(): void {
  grecaptcha.ready(async () => {
    await setRecaptcha();
    submitForm();
  });
}

function setRegistrationModel(): void {
  axios
    .get(`/api/register/${route.params.code}?skipJoined=${route.query.skipJoined ?? "false"}`)
    .then((res: AxiosResponse) => {
      if (res.headers.location) {
        redirect(res.headers.location);
      } else {
        model.value = res.data;
      }
    })
    .catch((reason: AxiosError) => {
      serverErrorOccurred.value = String(reason);
    });
}

onMounted(() => {
  if (!withInvite.value) {
    const script = document.createElement("script");
    script.type = "application/javascript";
    script.src = googleRecaptchaScript;
    document.head.appendChild(script);
  }

  if (isViewpointsInvite.value) {
    axios
      .get<InviteCodeSubjectApiModel>(`/api/invitecode/subject/${props.code}`)
      .then((response) => {
        viewpointsSubjectName.value = response.data.name;
        viewpointsSubjectFirstName.value = response.data.firstName;
      });
  }

  const source = route.query?.utm_source as string;
  if (source && typeof source === "string" && source.trim() !== "") {
    sessionStorage.setItem("utm_source", source);
  }

  if (v$.value) {
    v$.value.$reset();
  }

  if (withInvite.value) {
    setRegistrationModel();
  }
});

const isSubmitted = computed(() => submitStatus.value === "OK");

// this is so the name fields don't look wonky when only one has a validation error message appearing
function otherNameFieldHasValidationError(nameField: "firstName" | "lastName"): boolean {
  if (submitStatus.value === "Error") {
    return nameField === "firstName"
      ? !v$.value.lastName.required && v$.value.firstName.required
      : !v$.value.firstName.required && v$.value.lastName.required;
  }
  return false;
}
</script>

<style lang="less" scoped>
@import (reference) "../styles/bootstrap/bootstrap.less";
@import (reference) "../styles/bootstrap/variables.less";

@registration-text-color: @btn-disabled-color;

.registration-page {
  display: flex;
  min-height: 100vh;
  width: 100%;
  padding: 0;

  @media (max-width: @screen-sm-min) {
    flex-direction: column;
    gap: 2rem;
    height: unset;
  }
}

.registration-page__welcome {
  width: 55%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  background-color: @charismatic-color;

  @media (max-width: @screen-sm-min) {
    width: 100%;
    height: 35%;
  }
  @media (max-width: @screen-sm-min) {
    width: 100%;
    height: 35%;
  }
}

.welcome-image {
  width: 100%;
  max-width: 800px;
  @media (max-width: @screen-xs-min) {
    width: 50%;
  }
  @media (max-width: @screen-sm-min) {
    width: 40%;
  }
}

.registration-footer {
  position: absolute;
  bottom: 40px;
  width: 60%;

  @media (max-width: @screen-sm-min) {
    display: none;
  }
}

.registration-footer-list {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  list-style: none;

  .registration-footer-item {
    a {
      color: #ffffff;

      &:hover {
        color: @brand-primary;
        cursor: pointer;
      }
    }
  }
}

.name-field {
  position: relative;
  margin-top: auto;
}

.registration-page-right {
  padding: 3rem 1rem;
  width: 45%;
  display: flex;
  flex-direction: column;
  align-items: center;
  //overflow-y: scroll;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: @screen-sm-min) {
    width: 100%;
  }
}

.registration-page-right.success {
  display: flex;
  align-items: center;
  justify-content: center;
}

.registration-form-container {
  width: 75%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex-grow: 1;
  gap: 1rem;

  h1 {
    margin-top: 5px;
  }

  @media (max-width: @screen-sm-min) {
    width: 90%;
  }

  @media (min-width: @screen-xl) {
    width: 60%;
  }
}

.registration-form {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-around;
  width: 100%;
  //height: 90%;
  margin: 2rem 0 0;
  @media (max-width: @screen-sm-min) {
    margin-bottom: 0;
  }
}

@input-height: 60px;

.name-fields {
  display: grid;
  width: 100%;
  grid-template-columns: 1fr;
  @media (min-width: @screen-xs-min) {
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }
}

.registration-label-section {
  margin-bottom: 1.5rem;
  width: 100%;
  color: @registration-text-color;
  position: relative;
  display: flex;
  flex-direction: column;

  label {
    font-weight: normal;
  }
}

.registration-text-input {
  width: 100%;
  border: none;
  border-bottom: 1px solid rgba(@registration-text-color, 0.5);
  background-color: @registration-background-color;
  height: 35px;
  max-width: 100%;
  padding-left: 30px;

  &:focus,
  &:active {
    outline: none;
    border-bottom: 1px solid @registration-text-color;
  }

  &::placeholder {
    color: @practical-color;
  }
}

.placeholder-icon {
  position: absolute;
  bottom: 10px;
  left: 10px;

  img {
    height: 14px;
  }
}

.checkboxes {
  width: 100%;

  @media (max-width: @screen-sm-min) {
    margin-top: 2rem;
  }
}

.checkbox-default {
  margin-left: 1rem;
  width: 95%;
  font-size: 0.95rem;
  font-weight: 300;

  &:nth-child(1) {
    margin-bottom: 1rem;
  }

  input.required {
    border-color: red;
  }

  label {
    @media (max-width: @screen-sm-min) {
      max-width: 75%;
    }
  }

  .terms-link {
    color: @brand-primary;
  }
}

.btn {
  width: 90%;
  font-weight: bold;
  @media (max-width: @screen-sm-min) {
    margin-top: 10px;
  }
}

.info-icon {
  margin-left: 0.5rem;

  &:hover {
    cursor: pointer;
  }
}

.job-title-list-container {
  margin-top: 0;
  position: absolute;
  top: @input-height;
  left: 0;
  width: 100%;
  max-height: 15rem;
  overflow-y: scroll;
  z-index: 1;
  background-color: white;
  box-shadow: 0 5px 5px -5px rgba(0, 0, 0, 0.5);

  button {
    border: none;
    background-color: transparent;
    width: 100%;
    text-align: left;
    padding: 0.5rem 1rem;

    &:hover {
      background-color: @registration-background-color;
    }
  }
}

.job-title-options {
  padding: 1rem 0;
  list-style: none;
  border-bottom: 1px solid @registration-background-color;

  .job-title-list-item {
    cursor: pointer;
    padding: 0.25rem 1rem;

    &:hover {
      background-color: @registration-background-color;
    }
  }
}

.register-success,
.register-fail {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  width: 75%;
  position: relative;
  color: @registration-text-color;

  @media (max-width: @screen-sm-min) {
    top: -10%;
  }

  @media (max-width: @screen-sm-min) {
    top: -20%;
  }

  h2 {
    color: @text-color;
  }
}

.success-gif {
  width: 50px;
}

.resend-email-message {
  position: absolute;
  bottom: 40px;
  color: @registration-text-color;
}

.resend-email {
  color: @brand-primary;
}

.registration-submit-button {
  margin: 20px 0 20px;
  padding: 1rem 0;
  width: 80%;
  @media (max-width: @screen-sm-min) {
    margin-bottom: 20px;
  }
}

.viewpoints-invite-box {
  background-color: @transformational-color;
  border-radius: 8px;
  color: #ffffff;
  width: 75%;
  margin-bottom: 3rem;

  a {
    color: #ffffff;
    text-decoration: underline;
  }

  p {
    padding: 1rem 1rem;
    margin: 0;
  }
}
</style>

<style lang="less">
.password-tooltip .tooltip-inner {
  text-align: left;

  li {
    margin-bottom: 5px;
  }
}
</style>
